import {
    createTheme,
    Drawer,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    styled,
    type Theme,
    ThemeProvider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router';

import useAuthorization from '@@auth/hooks/useAuthorization';
import Icon, { type Props as IconProps } from '@@components/Icon/Icon';
import Image from '@@components/Image';
import config, { type NavigationItem } from '@@config';
import unityLogoBig from '@@images/unity-logo-big.png';
import { useSettingsStore } from '@@scripts/store/settings/useSettingsStore';
import { useReducedUI } from '@@scripts/store/useDevToolbarStore';
import { withShortcutHint } from '@@shortcuts/formatKeys';
import { useShortcutsDialog } from '@@shortcuts/useShortcutsDialog';
import { LinkBehavior } from '@@themes/mui/components';

const SHADOW_HEIGHT = 24;

const ITEM_HEIGHT = 48;

const ITEMS_WITH_SPACER = ['placement', 'categories'];

const ICON_NAMES = {
    articles: config.contentTypeToIconName.articles,
    tickers: config.contentTypeToIconName.tickers,
    slideshows: config.contentTypeToIconName.slideshows,
    videos: config.contentTypeToIconName.videos,
    links: config.contentTypeToIconName.links,
    embeds: config.contentTypeToIconName.embeds,
    breakingnews: config.contentTypeToIconName.breakingnews,
    placement: { name: 'grid-2', pack: 'regular' },
    curatedList: { name: 'document-star', pack: 'kit' },
    categories: { name: 'inbox' },
    sitemaps: { name: 'sitemap' },
    newsletters: { name: 'envelope' },
    collections: { name: 'clipboard-list' },
    tags: { name: 'tags' },
    geneeaEntities: { name: 'circle-bookmark' },
    kickwords: { name: 'flag' },
    teams: { name: 'users' },
    rtelements: { name: 'puzzle-piece' },
    users: { name: 'users-gear' },
    userSettings: { name: 'gear' },
    shortcuts: { name: 'circle-question', pack: 'sharp' },
} as const satisfies Record<string, IconProps>;

const Logo = styled(Image)(({ theme }) => ({
    display: 'block',
    height: '28px',
    margin: theme.spacing(3, 2, 1),
    objectFit: 'contain',
    objectPosition: '0 0',
}));

const NavList = styled(List)({
    flexGrow: 1,
    paddingTop: ITEM_HEIGHT,
    paddingBottom: ITEM_HEIGHT,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
});

const NavListItem = styled(ListItem, {
    shouldForwardProp: (prop: string) => !prop.startsWith('$'),
})<{ $spaced?: boolean }>(({ $spaced }) => ({
    marginTop: $spaced ? ITEM_HEIGHT : undefined,
}));

const NavButton = styled(ListItemButton)(({ theme }) => ({
    minHeight: ITEM_HEIGHT,
    paddingLeft: `calc(${theme.spacing(4)} / 2)`,
    paddingRight: `calc(${theme.spacing(4)} / 2)`,
})) as typeof ListItemButton;

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(5),
})) as typeof ListItemIcon;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    width: theme.fixed.navigation.collapsed.width,

    '& .MuiDrawer-paper': {
        width: theme.fixed.navigation.collapsed.width,
        transition: theme.transitions.create('width'),
        overflow: 'hidden',

        '&:hover': {
            width: theme.fixed.navigation.expanded.width,
        },

        '&:not(:hover)': {
            boxShadow: 'none',
        },
    },
})) as typeof Drawer;

const DrawerHeader = styled('div')(({ theme }) => ({
    boxShadow: `0px 0px ${SHADOW_HEIGHT}px ${SHADOW_HEIGHT}px ${theme.palette.background.paper}`,
    zIndex: 1,
}));

const DrawerFooter = styled(DrawerHeader)({
    marginTop: 'auto',
});

const generateTheme = (theme: Theme) =>
    createTheme(theme, {
        palette: {
            text: {
                primary: theme.palette.common.white,
            },
            background: {
                paper: theme.palette.primary['600'],
            },
            action: {
                active: theme.palette.common.white,
                focus: theme.palette.primary['700'],
                focusOpacity: 0.25,
                hover: theme.palette.primary['700'],
                hoverOpacity: 0.25,
                selectedOpacity: 0.75,
            },
            primary: {
                main: theme.palette.primary['800'],
            },
        },
    });

type Props = {
    navigation?: NavigationItem[];
};

/*
 * This comment is for translation auto extract
 * (all possible navigation items)
 t('navigation.breakingnews')
 t('navigation.categories')
 t('navigation.embeds')
 t('navigation.kickwords')
 t('navigation.links')
 t('navigation.placement')
 t('navigation.sitemaps')
 t('navigation.collections')
 t('navigation.slideshows')
 t('navigation.teams')
 t('navigation.tags')
 t('navigation.geneeaEntities')
 t('navigation.tickers')
 t('navigation.videos')
 t('navigation.users')
 t('navigation.rtelements')
 t('navigation.userSettings')
 t('navigation.shortcuts')
 */
const Navigation = ({ navigation = Object.values(config.navigation) }: Props) => {
    const { isAuthorized } = useAuthorization();
    const location = useLocation();

    const { t } = useTranslation();

    const reducedUI = useReducedUI();
    const betaFeaturesEnabled = useSettingsStore((state) => state.betaFeaturesEnabled);
    const { open: openShortcutsDialog } = useShortcutsDialog();

    const items = navigation.filter(
        (item) =>
            !item.excludeFromNavigationMenu &&
            !(reducedUI && item.excludeFromReducedUI) &&
            !(item.betaFeature && !betaFeaturesEnabled) &&
            isAuthorized(item.authorize),
    );

    const renderHeader = () => (
        // `display: flex` is needed to avoid collapsing `margin-top` when scrolling
        <Link href="/" title={t('page.title.home')} sx={{ display: 'flex' }}>
            <Logo src={unityLogoBig} alt="Unity Logo" />
        </Link>
    );

    const renderItemContent = (item: NavigationItem) => (
        <>
            <StyledListItemIcon>
                {/*
                 // @ts-expect-error TS7053 Issue #4120 */}
                <Icon {...ICON_NAMES[item.name]} size="large" />
            </StyledListItemIcon>

            <ListItemText
                primary={t(`navigation.${item.name}`)}
                slotProps={{
                    primary: { noWrap: true, variant: 'subtitle3' },
                }}
            />
        </>
    );

    const shortcutsItem: NavigationItem = { name: 'shortcuts', uri: '' };
    const userSettingsItem: NavigationItem = { name: 'userSettings', uri: 'userSettings' };

    return (
        <ThemeProvider theme={generateTheme}>
            <StyledDrawer variant="permanent">
                <DrawerHeader>{renderHeader()}</DrawerHeader>

                <NavList>
                    {items.map((item) => (
                        <NavListItem
                            key={item.name}
                            disablePadding
                            divider={false}
                            $spaced={ITEMS_WITH_SPACER.includes(item.name)}
                        >
                            <NavButton
                                divider={false}
                                component={LinkBehavior}
                                href={item.uri}
                                title={t(`navigation.${item.name}`)}
                                selected={Boolean(matchPath(item.uri, location.pathname))}
                            >
                                {renderItemContent(item)}
                            </NavButton>
                        </NavListItem>
                    ))}
                </NavList>

                <DrawerFooter>
                    <List disablePadding>
                        <ListItem disablePadding divider={false}>
                            <NavButton
                                divider={false}
                                title={withShortcutHint(t('navigation.shortcuts'), 'showShortcuts')}
                                onClick={openShortcutsDialog}
                            >
                                {renderItemContent(shortcutsItem)}
                            </NavButton>
                        </ListItem>

                        <ListItem disablePadding divider={false}>
                            <NavButton
                                divider={false}
                                component={LinkBehavior}
                                href={userSettingsItem.uri}
                                title={t(`navigation.${userSettingsItem.name}`)}
                            >
                                {renderItemContent(userSettingsItem)}
                            </NavButton>
                        </ListItem>
                    </List>
                </DrawerFooter>
            </StyledDrawer>
        </ThemeProvider>
    );
};

export default Navigation;
